import '../styles/LoginAccessPanel.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const DASHBOARD_PATH = '/dashboard';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(identityValue, credentialValue) {
  const errors = {};
  const id = String(identityValue || '').trim();
  if (!id) {
    errors.identity = 'Enter the identity you registered with.';
  } else if (!EMAIL_RE.test(id)) {
    errors.identity = 'Use a full identity address, like operator@brute.local.';
  }
  if (!credentialValue) {
    errors.credential = 'Enter your credential.';
  }
  return errors;
}

function LoginIdentityPoster() {
  return (
    <div className="lap-identity" id="lap-identity-poster">
      <span className="lap-sticker" role="note">
        <span className="lap-sticker__dot" aria-hidden="true" />
        Authorized targets only
      </span>

      <p className="lap-kicker">Identity check</p>
      <h1 className="lap-heading">
        <span className="lap-heading__line">Welcome</span>
        <span className="lap-heading__line">back,</span>
        <span className="lap-heading__line lap-heading__line--reversed">Operator</span>
      </h1>
      <p className="lap-note">
        Sign in to open your working surface and pick up your runs, findings and reports.
      </p>
      <p className="lap-trust">Local-first · Windows · No cloud required</p>
    </div>
  );
}

export default function LoginAccessPanel({ onVerifyIdentity }) {
  const { loading } = useProjectCollection('user_identity');

  const [identityValue, setIdentityValue] = useState('');
  const [credentialValue, setCredentialValue] = useState('');
  const [showCredential, setShowCredential] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState('idle');
  const [verificationError, setVerificationError] = useState('');
  const [lastAttemptIdentity, setLastAttemptIdentity] = useState('');

  const busy = submitStatus === 'submitting';

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const applyFailure = (message) => {
    setSubmitStatus('error');
    setVerificationError(message);
    setLastAttemptIdentity(identityValue);
    setCredentialValue('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;

    const errors = validate(identityValue, credentialValue);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmitStatus('error');
      setVerificationError('Check the highlighted fields and submit again.');
      return;
    }

    if (typeof onVerifyIdentity !== 'function') {
      setSubmitStatus('error');
      setVerificationError(
        'Credential verification is not available in this preview. Nothing was checked and no session was opened.'
      );
      return;
    }

    setSubmitStatus('submitting');
    setVerificationError('');

    let result;
    try {
      result = await onVerifyIdentity({
        identity: String(identityValue).trim(),
        credential: credentialValue,
      });
    } catch (err) {
      applyFailure(err && err.message ? err.message : 'Verification could not be reached. Try again.');
      return;
    }

    if (result && result.ok === true) {
      setSubmitStatus('success');
      const destination = result.redirectTo || DASHBOARD_PATH;
      window.location.assign(destination);
      return;
    }

    applyFailure(
      (result && (result.error || result.message)) ||
        'That identity and credential did not match. Your identity is kept — correct the credential and try again.'
    );
  };

  const handleRetry = () => {
    setVerificationError('');
    setSubmitStatus('idle');
    const el = document.getElementById('lap-identity-input');
    if (el) el.focus();
  };

  return (
    <section className="lap-root" aria-labelledby="lap-form-title">
      <div className="lap-rail">
        <div className="lap-poster">
          <LoginIdentityPoster />

          <div className="lap-form-panel">
            <div className="lap-form-head">
              <h2 className="lap-form-title" id="lap-form-title">
                Identity &amp; credential
              </h2>
              <span className="lap-form-step">{loading ? 'reading local identities…' : 'step 01 / 01'}</span>
            </div>

            {verificationError ? (
              <div className="lap-error" role="alert">
                <div className="lap-error__bar" aria-hidden="true" />
                <div className="lap-error__body">
                  <p className="lap-error__title">Verification failed</p>
                  <p className="lap-error__text">{verificationError}</p>
                  {lastAttemptIdentity && submitStatus === 'error' ? (
                    <button type="button" className="lap-error__retry" onClick={handleRetry}>
                      Try again — credential cleared
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            <form className="lap-form" onSubmit={handleSubmit} noValidate>
              <div className={`lap-field${fieldErrors.identity ? ' lap-field--invalid' : ''}`}>
                <label className="lap-label" htmlFor="lap-identity-input">
                  <span>Identity</span>
                  <span className="lap-label__hint">e.g. operator@brute.local</span>
                </label>
                <div className="lap-input-wrap">
                  <input
                    id="lap-identity-input"
                    name="identity"
                    className="lap-input"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    placeholder="you@brute.local"
                    value={identityValue}
                    onChange={(e) => {
                      setIdentityValue(e.target.value);
                      clearFieldError('identity');
                    }}
                    aria-invalid={fieldErrors.identity ? 'true' : 'false'}
                    aria-describedby={fieldErrors.identity ? 'lap-identity-error' : undefined}
                    disabled={busy}
                  />
                </div>
                {fieldErrors.identity ? (
                  <p className="lap-msg" id="lap-identity-error">
                    {fieldErrors.identity}
                  </p>
                ) : null}
              </div>

              <div className={`lap-field${fieldErrors.credential ? ' lap-field--invalid' : ''}`}>
                <label className="lap-label" htmlFor="lap-credential-input">
                  <span>Credential</span>
                  <span className="lap-label__hint">local only</span>
                </label>
                <div className="lap-input-wrap">
                  <input
                    id="lap-credential-input"
                    name="credential"
                    className="lap-input"
                    type={showCredential ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter credential"
                    value={credentialValue}
                    onChange={(e) => {
                      setCredentialValue(e.target.value);
                      clearFieldError('credential');
                    }}
                    aria-invalid={fieldErrors.credential ? 'true' : 'false'}
                    aria-describedby={fieldErrors.credential ? 'lap-credential-error' : undefined}
                    disabled={busy}
                  />
                  <button
                    type="button"
                    className="lap-reveal"
                    aria-pressed={showCredential}
                    aria-label={showCredential ? 'Hide credential' : 'Show credential'}
                    onClick={() => setShowCredential((v) => !v)}
                    disabled={busy}
                  >
                    {showCredential ? 'Hide' : 'Show'}
                  </button>
                </div>
                {fieldErrors.credential ? (
                  <p className="lap-msg" id="lap-credential-error">
                    {fieldErrors.credential}
                  </p>
                ) : null}
              </div>

              <div className="lap-submit-wrap">
                <button type="submit" className="lap-submit" disabled={busy} aria-busy={busy}>
                  {busy ? (
                    <span className="lap-busy">
                      <span className="lap-busy__segments" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                        <span />
                      </span>
                      Verifying…
                    </span>
                  ) : (
                    <>
                      <span>Verify &amp; open working surface</span>
                      <span className="lap-submit__arrow" aria-hidden="true">
                        →
                      </span>
                    </>
                  )}
                </button>
                <p className="lap-form-note">
                  Credentials are checked against your locally stored identities; nothing leaves this machine.
                </p>
              </div>
            </form>

            <div className="lap-support">
              <ul className="lap-support__list">
                <li>
                  <a className="lap-support__link" href="/sign-up">
                    <span>First time here? Create an identity</span>
                    <span aria-hidden="true">↗</span>
                  </a>
                </li>
                <li>
                  <a className="lap-support__link" href="/">
                    <span>Return to Landing</span>
                    <span aria-hidden="true">↖</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}